import React, { useEffect } from 'react';
import {
  FocusContext,
  FocusHandler,
  setFocus,
  useFocusable
} from '@noriginmedia/norigin-spatial-navigation';
import { ChangeEvent, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import Typography from '@smart-tv/components/typography/typography';
import { DialogItem } from '@smart-tv/components/dialog';
import { HEADER_SEARCH } from '@smart-tv/components/header/header-helpers';
import { topLevelPageReadyAtom } from '@smart-tv/atoms';
import { SearchResultInfo, SearchResultItem } from '@smart-tv/typings/search';
import search from '@smart-tv/utils/kmm/search';
import getSearchHistory from '@smart-tv/utils/kmm/getSearchHistory';
import removeFromSearchHistory from '@smart-tv/utils/kmm/removeFromSearchHistory';
import { debounce } from '@smart-tv/utils/debounce';
import { isBackKey } from '@smart-tv/utils/remoteKeys';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';
import useEventListener from '@smart-tv/hooks/use-event-listener';
import useNetwork from '@smart-tv/hooks/use-network/use-network';
import { networkError, BaseError } from '@smart-tv/contexts/error-context/error-context';
import { NetworkErrorDialog } from '@smart-tv/components/error-dialog/network-error-dialog';
import testIds from '@smart-tv/e2e/shared/variables';
import SearchResultCard from './search-result-card';
import useToast, { IconTypes } from '@smart-tv/hooks/use-toast/use-toast';

import styles from './search.module.scss';
import { Input } from '@smart-tv/components/input/input';
import { SEARCH_INPUT_FOCUS_KEY, getSearchHistoryRemoveFocusKey } from './search-focus-keys';

export { SEARCH_INPUT_FOCUS_KEY };

interface SearchAsideProps {
  searchValue: string;
  onInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  suggestions?: string[];
  onSuggestionClick: (suggestion: string) => void;
  hasResultsPaneItems: boolean;
}

const SearchAside: React.FC<SearchAsideProps> = ({
  searchValue,
  onInputChange,
  suggestions,
  onSuggestionClick,
  hasResultsPaneItems
}) => {
  const { ref, focusKey } = useFocusable({
    isFocusBoundary: true,
    focusBoundaryDirections: hasResultsPaneItems ? ['down'] : ['down', 'right']
  });

  return (
    <FocusContext.Provider value={focusKey}>
      <div className={styles['search__aside']} ref={ref}>
        <Typography variant="h2">Søg</Typography>
        <Input
          focusKey={SEARCH_INPUT_FOCUS_KEY}
          value={searchValue}
          onChange={onInputChange}
          placeholder="Søg efter film og serier"
          type="text"
          name="search"
          className={styles['search__input']}
          data-testid={testIds.search.input}
        />
        <div className={styles['search__suggestions']}>
          {suggestions && suggestions.length > 0 && (
            <>
              {suggestions.slice(0, 8).map((suggestion) => (
                <DialogItem
                  key={suggestion}
                  focusKey={`SEARCH_SUGGESTION_${suggestion}`}
                  onEnterPress={() => onSuggestionClick(suggestion)}
                  className={styles['search__suggestion']}
                >
                  {suggestion}
                </DialogItem>
              ))}
            </>
          )}
        </div>
      </div>
    </FocusContext.Provider>
  );
};

interface SearchResultsPaneProps {
  isSearching: boolean;
  searchResult: SearchResultInfo | null;
  previousSearches: SearchResultItem[];
  onResultFocus: FocusHandler;
  onAddToHistory: (item: SearchResultItem) => void;
  onRemoveFromHistory: (item: SearchResultItem) => void;
}

// Same reasoning as SearchAside - a real child component so its own boundary nests correctly.
const SearchResultsPane: React.FC<SearchResultsPaneProps> = ({
  isSearching,
  searchResult,
  previousSearches,
  onResultFocus,
  onAddToHistory,
  onRemoveFromHistory
}) => {
  const { ref, focusKey } = useFocusable({
    isFocusBoundary: true,
    focusBoundaryDirections: ['down']
  });

  return (
    <FocusContext.Provider value={focusKey}>
      <div className={styles['search__content']} ref={ref}>
        {!isSearching && searchResult && searchResult.results.length === 0 && (
          <Typography className={styles['search__content-subline']} variant="body">Der blev ikke fundet noget indhold</Typography>
        )}
        {!searchResult && previousSearches.length > 0 && (
          <Typography className={styles['search__content-headline']} variant="bodySmall">
            Seneste søgninger
          </Typography>
        )}

        <div className={styles['search__results']}>
          {(searchResult?.results ?? previousSearches).map((item, index) => (
            <div key={item.contentId} className={styles['search__result-item']}>
              <SearchResultCard
                item={item}
                index={index}
                onFocus={onResultFocus}
                onAdd={onAddToHistory}
                onRemove={searchResult ? undefined : () => onRemoveFromHistory(item)}
              />
            </div>
          ))}
        </div>
      </div>
    </FocusContext.Provider>
  );
};

export const Search: React.FC = () => {
  const setTopLevelPageReady = useSetAtom(topLevelPageReadyAtom);
  const { ref, focusKey, hasFocusedChild } = useFocusable({
    preferredChildFocusKey: SEARCH_INPUT_FOCUS_KEY,
    saveLastFocusedChild: false,
    trackChildren: true
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const {
    isOffline,
    setIsOffline,
    checkAndUpdateNetwork,
    isNetworkDialogSuppressed,
    setIsNetworkDialogSuppressed
  } = useNetwork();
  const handleBack = useCallback(
    (event: KeyboardEvent) => {
      if (IS_BROWSER || !hasFocusedChild || !isBackKey(event)) return;

      event.preventDefault();
      event.stopPropagation();
      setFocus(HEADER_SEARCH);
    },
    [hasFocusedChild]
  );

  useEventListener('keydown', handleBack);




  useEffect(() => {
    setTopLevelPageReady(true);
  }, [setTopLevelPageReady]);

  useEffect(() => {
    if (location.state?.focusFirst !== true) return;

    setFocus(SEARCH_INPUT_FOCUS_KEY);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const [searchValue, setSearchValue] = React.useState('');
  const [searchResult, setSearchResult] = React.useState<SearchResultInfo | null>(null);
  const [isSearching, setIsSearching] = React.useState(false);
  const [previousSearches, setPreviousSearches] = React.useState<SearchResultItem[]>([]);

  useEffect(() => {
    getSearchHistory(0, 15)
      .then(setPreviousSearches)
      .catch((error) => {
        console.error(error);
      });
  }, []);

  // Guards against an older, slower request overwriting a newer response.
  const latestQueryRef = React.useRef('');

  const runSearch = useCallback((query: string) => {
    latestQueryRef.current = query;
    setIsNetworkDialogSuppressed(false);

    if (!query.trim()) {
      setSearchResult(null);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    search(query)
      .then((result) => {
        if (latestQueryRef.current !== query) return;
        setSearchResult(result);
      })
      .catch((error) => {
        if (latestQueryRef.current !== query) return;
        if (error === networkError || (error as BaseError).offline) {
          setIsOffline(true);
          return;
        }
        console.error(error);
      })
      .finally(() => {
        if (latestQueryRef.current !== query) return;
        setIsSearching(false);
      });
  }, [setIsOffline, setIsNetworkDialogSuppressed]);

  const handleNetworkErrorRetry = useCallback(async () => {
    const hasNetwork = await checkAndUpdateNetwork();
    if (hasNetwork) runSearch(latestQueryRef.current);
  }, [checkAndUpdateNetwork, runSearch]);

  const handleNetworkErrorClose = useCallback(() => {
    setIsOffline(false);
    setIsNetworkDialogSuppressed(true);
  }, [setIsOffline, setIsNetworkDialogSuppressed]);

  // Avoids firing a search request on every keystroke.
  const debouncedRunSearch = React.useMemo(() => debounce(runSearch, 150), [runSearch]);

  const pendingFocusKeyRef = React.useRef<string>();

  useEffect(() => {
    if (pendingFocusKeyRef.current === undefined) return;

    setFocus(pendingFocusKeyRef.current);
    pendingFocusKeyRef.current = undefined;
  }, [previousSearches]);

  const handleRemoveFromHistory = useCallback(
    (item: SearchResultItem) => {
      setPreviousSearches((current) => {
        const index = current.findIndex((entry) => entry.contentId === item.contentId);
        const next = current.filter((entry) => entry.contentId !== item.contentId);
        const neighbor = next[Math.max(0, Math.min(index, next.length - 1))];
        pendingFocusKeyRef.current = neighbor
          ? getSearchHistoryRemoveFocusKey(neighbor.contentId)
          : SEARCH_INPUT_FOCUS_KEY;
        return next;
      });
      removeFromSearchHistory(item)
        .then((success) => {
          if (!success) throw new Error('removeFromSearchHistory returned false');
          showToast('Fjernet fra søgehistorik', IconTypes.success);
        })
        .catch((error) => {
          console.error(error);
          // Roll back the optimistic removal so the item doesn't vanish on a failed request.
          setPreviousSearches((current) =>
            current.some((entry) => entry.contentId === item.contentId)
              ? current
              : [item, ...current]
          );
          showToast('Kunne ikke fjerne fra søgehistorik', IconTypes.failure);
        });
    },
    [showToast]
  );

  // Keeps the previous-searches list in sync immediately, without waiting for a refetch.
  const handleAddToHistory = useCallback((item: SearchResultItem) => {
    setPreviousSearches((current) => [
      item,
      ...current.filter((entry) => entry.contentId !== item.contentId)
    ]);
  }, []);

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const query = event.target.value;
      setSearchValue(query);
      debouncedRunSearch(query);
    },
    [debouncedRunSearch]
  );

  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      setSearchValue(suggestion);
      debouncedRunSearch(suggestion);
    },
    [debouncedRunSearch]
  );

  // Mirrors collections.tsx: the focus system reports node.offsetTop relative to the
  // scrollable container itself, so scroll that container directly rather than the window.
  const scrollingRef = React.useRef<HTMLDivElement>(null);

  const handleResultFocus = useCallback(({ node }: { node: HTMLElement }) => {
    scrollingRef.current?.scrollTo({
      top: node.offsetTop - 240,
      behavior: 'auto'
    });
  }, []);

  // Switching between active search results and the previous-searches list swaps out
  // the whole grid, so keep it from staying scrolled to a position that no longer applies.
  useEffect(() => {
    scrollingRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  }, [Boolean(searchResult)]);

  const hasResultsPaneItems = searchResult
    ? searchResult.results.length > 0
    : previousSearches.length > 0;

  return (
    <FocusContext.Provider value={focusKey}>
      <div ref={ref} className={styles['search']}>
        <div className={styles['search__scroll-content']} ref={scrollingRef}>
          <div className={styles['search__container']}>
            <SearchAside
              searchValue={searchValue}
              onInputChange={handleInputChange}
              suggestions={searchResult?.suggestions}
              onSuggestionClick={handleSuggestionClick}
              hasResultsPaneItems={hasResultsPaneItems}
            />
            <SearchResultsPane
              isSearching={isSearching}
              searchResult={searchResult}
              previousSearches={previousSearches}
              onResultFocus={handleResultFocus}
              onAddToHistory={handleAddToHistory}
              onRemoveFromHistory={handleRemoveFromHistory}
            />
          </div>
        </div>
      </div>
      {isOffline && !isNetworkDialogSuppressed && (
        <NetworkErrorDialog onTryAgain={handleNetworkErrorRetry} onClose={handleNetworkErrorClose} />
      )}
    </FocusContext.Provider>
  );
};

export default Search;
